import { create } from 'zustand';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import type Graphic from '@arcgis/core/Graphic.js';

export type MeasurementMode = 'distance' | 'area';

export type MeasurementEntry = {
  id: string;
  mode: MeasurementMode;
  valueMeters: number;
  perimeterMeters?: number;
  label: string;
  geometry: Geometry;
  graphics: Graphic[];
};

type MeasurementsState = {
  entries: MeasurementEntry[];
  add: (entry: MeasurementEntry) => void;
  remove: (id: string) => void;
  clear: () => void;
};

let counter = 0;

export const useMeasurementsStore = create<MeasurementsState>((set) => ({
  entries: [],
  add: (entry) => set((s) => ({ entries: [...s.entries, entry] })),
  remove: (id) => set((s) => ({ entries: s.entries.filter((e) => e.id !== id) })),
  clear: () => set({ entries: [] }),
}));

export function nextMeasurementId(): string {
  counter += 1;
  return `meas_${counter.toString(36)}`;
}

export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters)) return '—';
  if (meters >= 1000) return `${(meters / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} km`;
  return `${meters.toLocaleString(undefined, { maximumFractionDigits: 1 })} m`;
}

export function formatArea(squareMeters: number): string {
  if (!Number.isFinite(squareMeters)) return '—';
  if (squareMeters >= 1_000_000) {
    return `${(squareMeters / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} km²`;
  }
  if (squareMeters >= 10_000) {
    return `${(squareMeters / 10_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} ha`;
  }
  return `${squareMeters.toLocaleString(undefined, { maximumFractionDigits: 1 })} m²`;
}
